import { FC } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Calendar,
  DollarSign,
  Mail,
  Phone,
  Clock,
  User,
} from 'lucide-react';
import { cn } from '../../../../utils/cn';
import { formatCurrency } from '../../../../utils/currency';
import { formatDate, formatRelativeDate, getInitials } from './utils';
import { ClientNotesEditor } from './ClientNotesEditor';
import type { ClientSummary } from '../../../../types/therapist-dashboard.types';

export interface ClientCardProps {
  client: ClientSummary;
  isExpanded: boolean;
  onToggleExpand: () => void;
  editingNotes: string;
  onNotesChange: (notes: string) => void;
  onSaveNotes: () => void;
  isSaving: boolean;
}

export const ClientCard: FC<ClientCardProps> = ({
  client,
  isExpanded,
  onToggleExpand,
  editingNotes,
  onNotesChange,
  onSaveNotes,
  isSaving,
}) => {

  return (
    <div
      className={cn(
        'bg-white dark:bg-slate-800 rounded-lg border transition-all',
        isExpanded
          ? 'border-blue-300 dark:border-blue-700 shadow-md'
          : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
      )}
    >
      {/* Client Header - Click to Expand */}
      <button
        type="button"
        onClick={onToggleExpand}
        className="w-full p-4 flex items-center gap-4 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset rounded-lg"
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for ${client.name}`}
      >
        {/* Avatar */}
        <div className="flex-shrink-0">
          {client.avatar ? (
            <img
              src={client.avatar}
              alt={client.name}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center">
              <span className="text-blue-700 dark:text-blue-300 font-semibold text-sm">
                {getInitials(client.name)}
              </span>
            </div>
          )}
        </div>

        {/* Client Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-slate-100 truncate">
            {client.name}
          </h3>
          <p className="text-sm text-gray-500 dark:text-slate-400 truncate">{client.email}</p>
        </div>

        {/* Stats - Hidden on mobile when collapsed */}
        <div
          className={cn(
            'hidden sm:flex items-center gap-6 text-sm',
            isExpanded ? '' : 'lg:flex'
          )}
        >
          <div className="text-center">
            <p className="font-semibold text-gray-900 dark:text-slate-100">
              {client.totalAppointments}
            </p>
            <p className="text-xs text-gray-500 dark:text-slate-400">Appointments</p>
          </div>
          <div className="text-center">
            <p className="font-semibold text-green-600 dark:text-green-400">
              {formatCurrency(client.totalSpent)}
            </p>
            <p className="text-xs text-gray-500 dark:text-slate-400">Total Spent</p>
          </div>
        </div>

        {/* Expand/Collapse Icon */}
        <div className="flex-shrink-0 text-gray-400 dark:text-slate-500">
          {isExpanded ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-100 dark:border-slate-700">
          <div className="pt-4 space-y-4">
            {/* Contact Info & Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Email */}
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                <a
                  href={`mailto:${client.email}`}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline truncate"
                >
                  {client.email}
                </a>
              </div>

              {/* Phone */}
              {client.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                  <a
                    href={`tel:${client.phone}`}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {client.phone}
                  </a>
                </div>
              )}

              {/* Total Appointments - Visible on mobile */}
              <div className="flex items-center gap-2 sm:hidden">
                <Calendar className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-slate-300">
                  {client.totalAppointments} appointments
                </span>
              </div>

              {/* Total Spent - Visible on mobile */}
              <div className="flex items-center gap-2 sm:hidden">
                <DollarSign className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                <span className="text-sm text-green-600 dark:text-green-400">
                  {formatCurrency(client.totalSpent)} total
                </span>
              </div>

              {/* Last Appointment */}
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-slate-300">
                  Last visit:{' '}
                  {client.lastAppointment ? formatRelativeDate(client.lastAppointment) : 'Never'}
                </span>
              </div>

              {/* Member Since */}
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-slate-300">
                  Client since {formatDate(client.joinedDate)}
                </span>
              </div>
            </div>

            {/* Preferred Services */}
            {client.preferredServices.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-2 uppercase tracking-wide">
                  Preferred Services
                </p>
                <div className="flex flex-wrap gap-2">
                  {client.preferredServices.map((service) => (
                    <span
                      key={service}
                      className="px-2.5 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full"
                    >
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Notes Section */}
            <ClientNotesEditor
              clientId={client.id}
              notes={editingNotes}
              originalNotes={client.notes || ''}
              onNotesChange={onNotesChange}
              onSaveNotes={onSaveNotes}
              isSaving={isSaving}
            />
          </div>
        </div>
      )}
    </div>
  );
};
